// Upload limits, formats and pre-upload image compression (UP-01).
// uploadLimits.json is a copy of server_elakay/elakay_config/uploadLimits.json
// (a backend test fails if they drift apart): the browser refuses a file
// before sending it with the same limit and message the server would use.
import LIMITS from "./uploadLimits.json";

const MiB = 1024 * 1024;

export const UPLOAD_LIMITS = LIMITS;

export const uploadFeature = (key) => {
  const entry = LIMITS.features[key];
  if (!entry) throw new Error(`Unknown upload feature: ${key}`);
  return entry;
};

export const formatMb = (bytes) => {
  const value = Number(bytes) / MiB;
  return Number.isInteger(value) ? String(value) : value.toFixed(1).replace(/\.0$/, "");
};

const typesOf = (key) => uploadFeature(key).types.map((type) => ({ key: type, ...LIMITS.types[type] }));

export const allowedLabel = (key) => [...new Set(typesOf(key).map((type) => type.label))].join(", ");

// Value for <input accept="...">.
export const acceptFor = (key) => typesOf(key).flatMap((type) => [...type.mimes, ...type.extensions]).join(",");

const extensionOf = (name) => {
  const match = /\.[^.]+$/.exec(String(name || "").toLowerCase());
  return match ? match[0] : "";
};

export const fileTypeOf = (key, file) => {
  const extension = extensionOf(file?.name);
  const mime = String(file?.type || "").toLowerCase();
  const types = typesOf(key);
  if (!extension) return types.find((type) => type.mimes.includes(mime)) || null;
  const byExtension = types.find((type) => type.extensions.includes(extension));
  if (!byExtension) return null;
  // Browsers leave the type empty for some documents (.docx, .heic).
  if (!mime || byExtension.mimes.includes(mime) || types.some((type) => type.mimes.includes(mime))) return byExtension;
  return ["pdf", "doc", "docx", "xls", "xlsx", "csv", "txt", "zip"].includes(byExtension.key) && mime === "application/octet-stream" ? byExtension : null;
};

export class UploadValidationError extends Error {
  constructor(code, message, extra = {}) {
    super(message);
    this.name = "UploadValidationError";
    this.code = code;
    Object.assign(this, extra);
  }
}

export const validateUploadFile = (key, file) => {
  const entry = uploadFeature(key);
  if (!file) return new UploadValidationError("NO_FILE", "Aucun fichier sélectionné.");
  if (!fileTypeOf(key, file)) {
    return new UploadValidationError("UNSUPPORTED_FILE_TYPE", `Format de fichier non accepté. Formats autorisés : ${allowedLabel(key)}.`, { allowed: allowedLabel(key) });
  }
  if (file.size > entry.maxBytes) {
    return new UploadValidationError("FILE_TOO_LARGE", `Fichier trop volumineux (${formatMb(file.size)} Mo). Taille maximale : ${formatMb(entry.maxBytes)} Mo.`, { maxBytes: entry.maxBytes, maxMb: Number(formatMb(entry.maxBytes)) });
  }
  return null;
};

// ── Image compression ────────────────────────────────────────────────────────
// Only when it pays off: a photo heavier than the target of its use is
// redrawn at a still generous size (2048–2560 px) and re-encoded at high
// quality (0.88). GIF (animation), SVG (vector), HEIC/AVIF (not decodable
// everywhere) and small files are always sent untouched. Never makes a file
// bigger: the original is kept when re-encoding does not help.
const COMPRESSION = {
  layoutBackground: { maxDimension: 2560, targetBytes: 2.5 * MiB },
  appPwaIcon: { maxDimension: 1024, targetBytes: 1.5 * MiB },
  socialPreview: { maxDimension: 2400, targetBytes: 3 * MiB },
  productImage: { maxDimension: 2400, targetBytes: 4 * MiB },
  productNoteImage: { maxDimension: 2048, targetBytes: 2 * MiB },
  shopImage: { maxDimension: 2560, targetBytes: 2.5 * MiB },
  categoryImage: { maxDimension: 2560, targetBytes: 2.5 * MiB },
  deliveryProof: { maxDimension: 2048, targetBytes: 1.5 * MiB },
  orderImage: { maxDimension: 1600, targetBytes: 0.9 * MiB },
  imageSearch: { maxDimension: 1280, targetBytes: 0.9 * MiB },
  emailImage: { maxDimension: 1920, targetBytes: 2 * MiB },
  chatbotMedia: { maxDimension: 2048, targetBytes: 3 * MiB },
  media: { maxDimension: 2560, targetBytes: 4 * MiB },
  swiperImage: { maxDimension: 2560, targetBytes: 4 * MiB },
  defaultPromoImage: { maxDimension: 2560, targetBytes: 4 * MiB },
  swiperImageFile: { maxDimension: 2560, targetBytes: 3 * MiB },
  document: { maxDimension: 2400, targetBytes: 3 * MiB },
  hwdiImage: { maxDimension: 2400, targetBytes: 3 * MiB },
  graphicPreview: { maxDimension: 2400, targetBytes: 3 * MiB },
};

const COMPRESSIBLE = new Set(["image/jpeg", "image/jpg", "image/png", "image/webp"]);

const loadBitmap = async (file) => {
  if (typeof createImageBitmap === "function") {
    try { return await createImageBitmap(file, { imageOrientation: "from-image" }); } catch (_) { /* fall back */ }
  }
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const image = new Image();
    image.onload = () => { URL.revokeObjectURL(url); resolve(image); };
    image.onerror = () => { URL.revokeObjectURL(url); reject(new Error("Image illisible.")); };
    image.src = url;
  });
};

const hasTransparency = (context, width, height) => {
  try {
    const step = Math.max(1, Math.floor(Math.max(width, height) / 64));
    const { data } = context.getImageData(0, 0, width, height);
    for (let y = 0; y < height; y += step) {
      for (let x = 0; x < width; x += step) if (data[(y * width + x) * 4 + 3] < 255) return true;
    }
  } catch (_) { return true; }
  return false;
};

const encode = (canvas, type, quality) => new Promise((resolve) => canvas.toBlob(resolve, type, quality));

export const compressImageForUpload = async (key, file) => {
  const settings = COMPRESSION[key];
  if (!settings || !file || !COMPRESSIBLE.has(String(file.type).toLowerCase()) || file.size <= settings.targetBytes) return file;
  if (typeof document === "undefined") return file;
  let bitmap;
  try { bitmap = await loadBitmap(file); } catch (_) { return file; }
  const types = uploadFeature(key).types;
  const maxBytes = uploadFeature(key).maxBytes;
  const attempts = [
    { scale: 1, quality: 0.88 },
    { scale: 0.85, quality: 0.82 },
    { scale: 0.7, quality: 0.78 },
  ];
  let best = file;
  for (const attempt of attempts) {
    const longest = Math.max(bitmap.width, bitmap.height);
    const ratio = Math.min(1, (settings.maxDimension * attempt.scale) / longest);
    const width = Math.max(1, Math.round(bitmap.width * ratio));
    const height = Math.max(1, Math.round(bitmap.height * ratio));
    const canvas = document.createElement("canvas");
    canvas.width = width; canvas.height = height;
    const context = canvas.getContext("2d");
    if (!context) return file;
    context.drawImage(bitmap, 0, 0, width, height);
    const transparent = file.type !== "image/jpeg" && hasTransparency(context, width, height);
    // Transparency is kept with WebP; opaque photos become JPEG (or WebP when
    // the feature does not accept JPEG).
    const target = transparent ? (types.includes("webp") ? "image/webp" : null)
      : (types.includes("jpeg") ? "image/jpeg" : types.includes("webp") ? "image/webp" : null);
    if (!target) return file;
    const blob = await encode(canvas, target, attempt.quality);
    if (!blob || blob.type !== target) return best;
    const extension = target === "image/webp" ? ".webp" : ".jpg";
    const candidate = new File([blob], `${String(file.name || "image").replace(/\.[^.]+$/, "") || "image"}${extension}`, { type: target, lastModified: Date.now() });
    if (candidate.size < best.size) best = candidate;
    if (best.size <= settings.targetBytes || best.size <= maxBytes * 0.9) break;
  }
  if (typeof bitmap.close === "function") bitmap.close();
  return best;
};

// Validate the type, compress when useful, then check the size. Resolves the
// file to send, rejects with an UploadValidationError (French message).
export const prepareUpload = async (key, file) => {
  const typeError = validateUploadFile(key, file);
  if (typeError && typeError.code !== "FILE_TOO_LARGE") throw typeError;
  const prepared = await compressImageForUpload(key, file);
  const sizeError = validateUploadFile(key, prepared);
  if (sizeError) throw sizeError;
  return prepared;
};

// Total of one multipart request must stay under the proxy ceiling.
export const proxyLimitFor = (endpoint = "") => (
  LIMITS.proxy.largeRoutes.some((route) => String(endpoint).includes(route.replace("/elakay_api", "")))
    ? LIMITS.proxy.largeRequestBytes : LIMITS.proxy.defaultRequestBytes
);

// One multipart request must fit the nginx ceiling of its route AND
// Cloudflare's 100 MB request cap, with room for the multipart envelope.
export const requestCeilingFor = (endpoint = "") => Math.min(proxyLimitFor(endpoint), LIMITS.proxy.cloudflareRequestBytes) - LIMITS.proxy.multipartOverheadBytes;

export const assertRequestFits = (files, endpoint) => {
  const total = files.filter(Boolean).reduce((sum, file) => sum + (file.size || 0), 0);
  const ceiling = requestCeilingFor(endpoint);
  if (total > ceiling) {
    throw new UploadValidationError("REQUEST_TOO_LARGE", `Les fichiers envoyés ensemble sont trop volumineux (${formatMb(total)} Mo). Maximum par envoi : ${formatMb(ceiling)} Mo.`, { maxBytes: ceiling });
  }
  return total;
};

// Splits files into batches whose total stays under `maxBytes` (and at most
// `maxFiles` files per batch).
export const batchFiles = (files, { maxBytes, maxFiles = Infinity }) => {
  const batches = [];
  let current = []; let total = 0;
  for (const file of files) {
    if (current.length && (total + file.size > maxBytes || current.length >= maxFiles)) { batches.push(current); current = []; total = 0; }
    current.push(file); total += file.size;
  }
  if (current.length) batches.push(current);
  return batches;
};
